import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setRequested, setReceived, setFailed } from "modules/loader-watchers/helpers/loading"

// Actions for fetching journal note
const crewMemberJournalNoteRequested = createAction("crewMemberJournal/noteRequested")
const crewMemberJournalNoteReceived = createAction("crewMemberJournal/noteReceived")
const crewMemberJournalNoteFailed = createAction("crewMemberJournal/noteFailed")

// Actions for creating journal note
const crewMemberJournalCreateNoteRequested = createAction("crewMemberJournal/createNoteRequested")
const crewMemberJournalCreateNoteReceived = createAction("crewMemberJournal/createNoteReceived")
const crewMemberJournalCreateNoteFailed = createAction("crewMemberJournal/createNoteFailed")

// Actions for updating journal note
const crewMemberJournalUpdateNoteRequested = createAction("crewMemberJournal/updateNoteRequested")
const crewMemberJournalUpdateNoteReceived = createAction("crewMemberJournal/updateNoteReceived")
const crewMemberJournalUpdateNoteFailed = createAction("crewMemberJournal/updateNoteFailed")

// Actions for fetching trip locations
const crewMemberTripLocationsRequested = createAction("crewMemberJournal/tripLocationsRequested")
const crewMemberTripLocationsReceived = createAction("crewMemberJournal/tripLocationsReceived")
const crewMemberTripLocationsFailed = createAction("crewMemberJournal/tripLocationsFailed")

// Actions for fetching journal versions
const crewMemberJournalVersionsRequested = createAction("crewMemberJournal/versionsRequested")
const crewMemberJournalVersionsReceived = createAction("crewMemberJournal/versionsReceived")
const crewMemberJournalVersionsFailed = createAction("crewMemberJournal/versionsFailed")

// API action creators - Using unified endpoint with profile_id parameter
export const getCrewMemberJournalNote = (crewMemberId, date) =>
  api({
    url: `/guide/journal_notes?date=${date}&profile_id=${crewMemberId}`,
    onStart: crewMemberJournalNoteRequested.type,
    onSuccess: crewMemberJournalNoteReceived.type,
    onError: crewMemberJournalNoteFailed.type
  })

export const createCrewMemberJournal = (crewMemberId, data) =>
  api({
    url: `/guide/journal_notes?profile_id=${crewMemberId}`,
    method: "post",
    onStart: crewMemberJournalCreateNoteRequested.type,
    onSuccess: crewMemberJournalCreateNoteReceived.type,
    onError: crewMemberJournalCreateNoteFailed.type,
    data
  })

export const updateCrewMemberJournal = (crewMemberId, id, data) =>
  api({
    url: `/guide/journal_notes/${id}?profile_id=${crewMemberId}`,
    method: "put",
    data: data,
    onStart: crewMemberJournalUpdateNoteRequested.type,
    onSuccess: crewMemberJournalUpdateNoteReceived.type,
    onError: crewMemberJournalUpdateNoteFailed.type
  })

export const saveCrewMemberJournal = (crewMemberId, id, data) =>
  id ? updateCrewMemberJournal(crewMemberId, id, data) : createCrewMemberJournal(crewMemberId, data)

export const getCrewMemberTripLocations = () =>
  api({
    url: "/guide/calendar/guide_trip_locations",
    onStart: crewMemberTripLocationsRequested.type,
    onSuccess: crewMemberTripLocationsReceived.type,
    onError: crewMemberTripLocationsFailed.type
  })

export const getCrewMemberJournalVersions = (id, params = {}) =>
  api({
    url: `/guide/journal_notes/${id}/versions`,
    params: { ...params },
    onStart: crewMemberJournalVersionsRequested.type,
    onSuccess: crewMemberJournalVersionsReceived.type,
    onError: crewMemberJournalVersionsFailed.type
  })

// Initial state
const initialState = {
  journalNote: {
    ...LOADING_INITIAL_STATE,
    data: null
  },
  tripLocations: {
    ...LOADING_INITIAL_STATE,
    data: []
  },
  journalVersions: {
    ...LOADING_INITIAL_STATE,
    data: {
      versions: [],
      pagination: null
    }
  }
}

// Reducer
const crewMemberJournalReducer = createReducer(initialState, (builder) => {
  builder
    // Fetch journal note
    .addCase(crewMemberJournalNoteRequested, (state) => {
      setRequested(state.journalNote)
      state.journalNote.data = null
    })
    .addCase(crewMemberJournalNoteReceived, (state, action) => {
      setReceived(state.journalNote)
      state.journalNote.data = action.payload || null
    })
    .addCase(crewMemberJournalNoteFailed, (state) => {
      setFailed(state.journalNote)
      state.journalNote.data = null
    })
    // Create journal note
    .addCase(crewMemberJournalCreateNoteRequested, (state) => {
      setRequested(state.journalNote)
    })
    .addCase(crewMemberJournalCreateNoteReceived, (state, action) => {
      setReceived(state.journalNote)
      state.journalNote.data = action.payload
    })
    .addCase(crewMemberJournalCreateNoteFailed, (state) => {
      setFailed(state.journalNote)
    })
    // Update journal note
    .addCase(crewMemberJournalUpdateNoteRequested, (state) => {
      setRequested(state.journalNote)
    })
    .addCase(crewMemberJournalUpdateNoteReceived, (state, action) => {
      setReceived(state.journalNote)
      state.journalNote.data = action.payload
    })
    .addCase(crewMemberJournalUpdateNoteFailed, (state) => {
      setFailed(state.journalNote)
    })
    // Fetch trip locations
    .addCase(crewMemberTripLocationsRequested, (state) => {
      setRequested(state.tripLocations)
      state.tripLocations.data = []
    })
    .addCase(crewMemberTripLocationsReceived, (state, action) => {
      setReceived(state.tripLocations)
      state.tripLocations.data = action.payload?.trip_locations || []
    })
    .addCase(crewMemberTripLocationsFailed, (state) => {
      setFailed(state.tripLocations)
      state.tripLocations.data = []
    })
    // Fetch journal versions
    .addCase(crewMemberJournalVersionsRequested, (state) => {
      setRequested(state.journalVersions)
    })
    .addCase(crewMemberJournalVersionsReceived, (state, action) => {
      setReceived(state.journalVersions)
      state.journalVersions.data = {
        versions: action.payload?.versions || [],
        pagination: action.payload?.pagination || null
      }
    })
    .addCase(crewMemberJournalVersionsFailed, (state) => {
      setFailed(state.journalVersions)
      state.journalVersions.data = {
        versions: [],
        pagination: null
      }
    })
})

export default crewMemberJournalReducer
